import * as React from 'react';
import styled from 'styled-components';

import { theme } from 'styles/utils';

import Container from 'components/Container';
import Icon from 'components/Icon';
import Stack from 'components/Stack';
import Starfield from 'components/Starfield';
import Text from 'components/Text';
import VisuallyHidden from 'components/VisuallyHidden';

const Section = styled.section`
  padding: 4.25rem 1rem;
  position: relative;
  overflow: hidden;
`;

const StyledStarfield = styled(Starfield)`
  mask-image: linear-gradient(to bottom, #fff0, #fff 5%, #fff 75%, #fff0);
`;

const TableWrapper = styled.div`
  align-self: center;
  inline-size: fit-content;
  margin-inline-end: -1rem;
  margin-inline-start: -1rem;
  max-inline-size: calc(100% + 2rem);

  & > * {
    margin-inline-end: 1rem;
    margin-inline-start: 1rem;
  }
`;

const Table = styled.table`
  --cell-bg-color: 35 106 145;

  border-collapse: separate;
  border-spacing: 0;
  text-wrap: pretty;
  inline-size: fit-content;

  th {
    font-family: ${theme('fonts.heading')};
    font-size: 1.125rem;
    font-weight: bold;
    padding: 1.25rem 1rem;
  }

  td {
    background-image: radial-gradient(
        ellipse 100% 12rem at 50% -8rem,
        rgb(var(--cell-bg-color) / 40%),
        rgb(var(--cell-bg-color) / 0%)
      ),
      linear-gradient(
        rgb(var(--cell-bg-color) / 10%) 35%,
        rgb(var(--cell-bg-color) / 0%)
      );
    border: 1px solid rgba(var(--cell-bg-color) / 10%);
    font-size: 0.875rem;
    line-height: 1.75;
    min-width: min(9rem, 15vw);
    padding: 1rem;
    text-align: center;
  }

  td:has([data-icon='no']) {
    --cell-bg-color: 255 94 94;
    color: #d01b00;
  }

  td:has([data-icon='yes']) {
    --cell-bg-color: 0 214 71;
    color: #228e71;
  }

  td:first-of-type {
    border-left-color: rgb(255 255 255 / 35%);
    text-align: left;
    width: 32rem;
    min-width: min(16rem, 55vw);
  }

  td:last-of-type {
    border-right-color: rgb(255 255 255 / 35%);
  }

  tr:first-of-type {
    td {
      border-top-color: rgb(255 255 255 / 35%);
    }

    td:first-of-type {
      border-top-left-radius: 0.625rem;
    }

    td:last-of-type {
      border-top-right-radius: 0.625rem;
    }
  }

  tr:last-of-type {
    td {
      border-bottom-color: rgb(255 255 255 / 35%);
    }

    td:first-of-type {
      border-bottom-left-radius: 0.625rem;
    }

    td:last-of-type {
      border-bottom-right-radius: 0.625rem;
    }
  }
`;

const StyledIcon = styled(Icon)``;

const features = [
  'Focus on proven metrics like DORA, DevEx, & SPACE—not every metric',
  'Automated metrics for your rituals: board updates, eng reviews & more',
  'Adopt any Reviews type to start—skip the busy dashboard',
  'Drive outcomes through a familiar, code review-like process',
  'Map resources, status, and outcomes to business goals',
  'AI powered insights and recommendations',
];

export default function SleuthDifferenceFeatureSection() {
  return (
    <Section>
      <StyledStarfield />
      <Container as={Stack} gap="2.625rem" size="md">
        <Text as="h2" variant="heading.section">
          Achieve success with every metrics program
        </Text>
        <Text variant="md">
          When metrics programs fail in, it's rarely due to a lack of top-down
          support. While tracking the right metrics is essential, the real
          challenge lies in how you use them (hint: dashboards won't cut it!).
          Most engineering intelligence tools stop at dashboards.
        </Text>
        <TableWrapper>
          <Table>
            <VisuallyHidden as="caption">
              Comparison of Sleuth to other tools
            </VisuallyHidden>
            <thead>
              <tr>
                <th>
                  <VisuallyHidden>Product feature</VisuallyHidden>
                </th>
                <th>
                  <VisuallyHidden>Supported by</VisuallyHidden>
                  Other tools
                </th>
                <th>
                  <VisuallyHidden>Supported by</VisuallyHidden>
                  Sleuth
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature) => (
                <tr key={feature}>
                  <td>
                    <p>{feature}</p>
                  </td>
                  <td>
                    <StyledIcon data-icon="no" name="no" size="xl" />
                    <VisuallyHidden>No</VisuallyHidden>
                  </td>
                  <td>
                    <StyledIcon data-icon="yes" name="yes" size="xl" />
                    <VisuallyHidden>Yes</VisuallyHidden>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Container>
    </Section>
  );
}
